import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  DatePicker,
  Form,
  Image,
  Input,
  InputNumber,
  Message,
  Modal,
  Popconfirm,
  Spin,
  Switch,
  Tag,
  Upload
} from '@arco-design/web-react';
import { IconCheck, IconDelete, IconEdit, IconPlus } from '@arco-design/web-react/icon';
import dayjs from 'dayjs';

import { get, post } from '../requestNew';
import { API_CONFIG } from '../utils';
import './index.scss';

const FormItem = Form.Item;
const URL_UPLOAD = API_CONFIG.uploadUrl;

const STATUS_BACKUP = 0;
const STATUS_ACTIVE = 1;

type UmsReviewGroupQrcode = {
  id: number;
  groupName: string;
  qrCodeUrl: string;
  status: number;
  joinedCount: number;
  maxMemberCount: number;
  expireDate?: string;
  isValid: number;
  createTime?: string;
  updateTime?: string;
};

type ReviewGroupQrcodeListResp = {
  activeCount: number;
  backupCount: number;
  list: UmsReviewGroupQrcode[];
};

const formItemLayout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 16 }
};

const toDateTimeText = (value?: string) => {
  if (!value) return '-';
  const parsed = dayjs(value);
  if (!parsed.isValid()) return '-';
  return parsed.format('YYYY-MM-DD HH:mm:ss');
};

const toDateText = (value?: string) => {
  if (!value) return '-';
  const parsed = dayjs(value);
  if (!parsed.isValid()) return '-';
  return parsed.format('YYYY-MM-DD');
};

const getUploadUrl = (file: any) => file?.url || file?.response?.data || '';

function ReviewGroupQrcodeManagement() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<number | null>(null);
  const [activeCount, setActiveCount] = useState(0);
  const [backupCount, setBackupCount] = useState(0);
  const [dataList, setDataList] = useState<UmsReviewGroupQrcode[]>([]);
  const [visible, setVisible] = useState(false);
  const [qrFileList, setQrFileList] = useState<any[]>([]);
  const [editingRecord, setEditingRecord] = useState<UmsReviewGroupQrcode | null>(null);

  const fetchList = useCallback(() => {
    setLoading(true);
    get<ReviewGroupQrcodeListResp>('/review/groupQrcode/list')
      .then((res: any) => {
        if (res?.code === 200 && res?.data) {
          setActiveCount(res.data.activeCount ?? 0);
          setBackupCount(res.data.backupCount ?? 0);
          setDataList(res.data.list || []);
        } else {
          Message.error(res?.message || '获取列表失败');
        }
      })
      .catch(() => {
        Message.error('获取列表失败');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const resetModal = () => {
    setEditingRecord(null);
    form.resetFields();
    setQrFileList([]);
    setVisible(false);
  };

  const openAddModal = () => {
    setEditingRecord(null);
    form.resetFields();
    setQrFileList([]);
    form.setFieldsValue({
      maxMemberCount: 200
    });
    setVisible(true);
  };

  const openEditModal = (record: UmsReviewGroupQrcode) => {
    setEditingRecord(record);
    form.resetFields();
    setQrFileList(record.qrCodeUrl ? [{ uid: `qr-${record.id}`, url: record.qrCodeUrl, name: 'qrcode' }] : []);
    form.setFieldsValue({
      groupName: record.groupName,
      qrCodeUrl: record.qrCodeUrl,
      maxMemberCount: record.maxMemberCount ?? 200,
      expireDate: record.expireDate ? dayjs(record.expireDate) : undefined,
      isActive: record.status === STATUS_ACTIVE
    });
    setVisible(true);
  };

  const handleQrChange = (fileList: any[]) => {
    const latest = fileList.slice(-1);
    const latestUrl = getUploadUrl(latest[0]);
    setQrFileList(latest);
    form.setFieldValue('qrCodeUrl', latestUrl || '');
    if (latestUrl) {
      Message.success('上传成功');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validate();
      const payload: Record<string, any> = {
        groupName: values.groupName?.trim(),
        qrCodeUrl: values.qrCodeUrl,
        maxMemberCount: values.maxMemberCount
      };
      if (values.expireDate) {
        payload.expireDate = dayjs(values.expireDate).format('YYYY-MM-DD');
      }

      setSubmitLoading(true);
      const editing = editingRecord;
      if (editing?.id) {
        await post(`/review/groupQrcode/update/${editing.id}`, payload);
        Message.success('编辑成功');
      } else {
        await post('/review/groupQrcode/add', payload);
        Message.success('新增成功');
      }
      resetModal();
      fetchList();
    } catch (error: any) {
      if (error?.errorFields) {
        Message.error('请完善表单信息');
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleActivate = (record: UmsReviewGroupQrcode) => {
    if (!record?.id) return;
    setActionLoadingId(record.id);
    post(`/review/groupQrcode/activate/${record.id}`)
      .then((res: any) => {
        if (res?.code === 200) {
          Message.success('已设为生效');
          fetchList();
        } else {
          Message.error(res?.message || '设为生效失败');
        }
      })
      .catch(() => {
        Message.error('设为生效失败');
      })
      .finally(() => {
        setActionLoadingId(null);
      });
  };

  const handleDelete = (record: UmsReviewGroupQrcode) => {
    if (!record?.id) return;
    if (record.status === STATUS_ACTIVE) {
      Message.warning('生效中的二维码不可删除，请先切换其他二维码为生效');
      return;
    }
    setActionLoadingId(record.id);
    post(`/review/groupQrcode/delete/${record.id}`)
      .then((res: any) => {
        if (res?.code === 200) {
          Message.success('删除成功');
          fetchList();
        } else {
          Message.error(res?.message || '删除失败');
        }
      })
      .catch(() => {
        Message.error('删除失败');
      })
      .finally(() => {
        setActionLoadingId(null);
      });
  };

  const renderQrcodeItem = (record: UmsReviewGroupQrcode) => {
    const isActive = record.status === STATUS_ACTIVE;
    const itemLoading = actionLoadingId === record.id;

    return (
      <div
        key={record.id}
        className={`qrcode-item ${isActive ? 'is-active' : 'is-backup'}`}
      >
        <div className="qrcode-image-wrap">
          {record.qrCodeUrl ? (
            <Image src={record.qrCodeUrl} width={96} height={96} preview={true} />
          ) : (
            <div style={{ width: '100%', height: '100%', background: '#f2f3f5' }} />
          )}
        </div>

        <div className="qrcode-info">
          <div className="qrcode-title-row">
            <span className="qrcode-title">{record.groupName || '-'}</span>
            <Tag color={isActive ? 'green' : 'gray'}>{isActive ? '使用中' : '已停用'}</Tag>
          </div>
          <div className="qrcode-meta">
            <span>
              已加入 {record.joinedCount ?? 0} / {record.maxMemberCount ?? 0} 人
            </span>
            <span>创建时间 {toDateTimeText(record.createTime)}</span>
            {isActive ? (
              <span>有效期至 {toDateText(record.expireDate)}</span>
            ) : (
              <span>修改时间 {toDateTimeText(record.updateTime)}</span>
            )}
          </div>
        </div>

        <div className="qrcode-actions">
          {isActive ? (
            <Button type="outline" status="success" disabled icon={<IconCheck />}>
              当前生效
            </Button>
          ) : (
            <Button
              type="primary"
              loading={itemLoading}
              onClick={() => handleActivate(record)}
            >
              设为生效
            </Button>
          )}
          <Button
            type="outline"
            icon={<IconEdit />}
            loading={itemLoading}
            onClick={() => openEditModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="提示"
            content="是否确认删除该二维码？"
            disabled={isActive}
            onOk={() => handleDelete(record)}
          >
            <Button
              type="outline"
              status="danger"
              icon={<IconDelete />}
              loading={itemLoading}
              disabled={isActive}
            >
              删除
            </Button>
          </Popconfirm>
        </div>
      </div>
    );
  };

  return (
    <div className="review-group-qrcode-page">
      <div className="page-header">
        <h2 className="page-title">复盘进群二维码管理</h2>
        <Button type="primary" icon={<IconPlus />} onClick={openAddModal}>
          新增二维码
        </Button>
      </div>

      <div className="stats-row">
        <div className="stats-card active">
          <div className="stats-label">当前生效群组</div>
          <div className="stats-value">{activeCount} 个</div>
        </div>
        <div className="stats-card backup">
          <div className="stats-label">备用群组</div>
          <div className="stats-value">{backupCount} 个</div>
        </div>
      </div>

      <Spin loading={loading} style={{ width: '100%' }}>
        <div className="qrcode-list">
          {dataList.length > 0 ? (
            dataList.map(renderQrcodeItem)
          ) : (
            !loading && <div className="empty-list">暂无二维码数据</div>
          )}
        </div>
      </Spin>

      <div className="notice-box">
        <div className="notice-title">注意事项</div>
        同一时间仅能有一个二维码生效。切换生效二维码后，旧二维码将立即停止扫描入群功能。请确保备用二维码已准备就绪再进行切换操作，以免影响用户体验。
      </div>

      <Modal
        title={editingRecord?.id ? '编辑二维码' : '新增二维码'}
        visible={visible}
        onCancel={resetModal}
        onOk={handleSubmit}
        confirmLoading={submitLoading}
        unmountOnExit
        style={{ width: 560 }}
      >
        <Form {...formItemLayout} form={form}>
          <FormItem
            label="群名"
            field="groupName"
            rules={[{ required: true, message: '请输入群名' }]}
          >
            <Input placeholder="请输入..." maxLength={64} />
          </FormItem>

          <FormItem label="二维码" required>
            <FormItem field="qrCodeUrl" noStyle rules={[{ required: true, message: '请上传二维码' }]}>
              <Input style={{ display: 'none' }} />
            </FormItem>
            <Upload
              listType="picture-card"
              imagePreview
              limit={1}
              accept="image/*"
              action={URL_UPLOAD}
              headers={{ Authorization: `${localStorage.getItem('authToken')}` }}
              fileList={qrFileList}
              onChange={handleQrChange}
            />
          </FormItem>

          {editingRecord ? (
            <FormItem label="是否生效" field="isActive" triggerPropName="checked">
              <Switch disabled checkedText="使用中" uncheckedText="已停用" />
            </FormItem>
          ) : null}

          <FormItem
            label="人数上限"
            field="maxMemberCount"
            rules={[{ required: true, message: '请输入人数上限' }]}
          >
            <InputNumber min={1} precision={0} placeholder="请输入..." style={{ width: '100%' }} />
          </FormItem>

          <FormItem label="有效期至" field="expireDate">
            <DatePicker placeholder="请选择日期" style={{ width: '100%' }} format="YYYY-MM-DD" />
          </FormItem>
        </Form>
      </Modal>
    </div>
  );
}

export default ReviewGroupQrcodeManagement;
